import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box, Stack, Grid, List, ListItem, ListItemText, Paper } from '@mui/material';
import { GOAL_STATUSES } from './constants';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { TimeIntervalTypeEnum } from '../../../Enum/TimeIntervalTypeEnum';
import { getRelated } from './utils';
import { isValid, isAfter, isSameDay, subDays, isBefore } from 'date-fns';
import { toDate } from '../../../utils/dateHelpers';
import { CarePlanEnum } from '../../../Enum/CarePlanEnum';
import { useParams } from 'react-router-dom';
import { handleSendToMember, setShowSignatureModal, setSignatureType } from '../../../store/carePlanUpdate.slice';
import { CarePlanSignatureTypeEnum } from '../../../Enum/CarePlanSignatureTypeEnum';

interface ReviewSignModalProps {
    open: boolean;
    onClose: () => void;
    reviewSignGoals: any[];
}

export const ReviewSignModal: React.FC<ReviewSignModalProps> = ({ open, onClose, reviewSignGoals }) => {
    const { integratedCarePlan, memberSignatureRequest, providerSignatureRequest } = useSelector((state: RootState) => state.carePlanUpdate);
    const params = useParams();
    const memberId = params.memberId || '';

    const mostRecentlyUpdatedGoal = reviewSignGoals.reduce((latest, goal) => {
        const goalUpdatedAt = toDate(goal.UpdatedAt);
        const latestDate = toDate(latest) || new Date(0);
        return goalUpdatedAt && isAfter(goalUpdatedAt, latestDate) ? goalUpdatedAt : latestDate;
    }, new Date(0));

    const providerLastReviewed = toDate(providerSignatureRequest?.LastReviewedOn);
    const planLastUpdated = toDate(integratedCarePlan?.LastUpdatedTime);
    const cutoffDate = subDays(new Date(), 90);

    const isSameOrAfter = (date1: Date, date2: Date) => isAfter(date1, date2) || isSameDay(date1, date2);

    const showProviderSignature =
        providerSignatureRequest &&
        providerLastReviewed &&
        planLastUpdated &&
        isSameOrAfter(providerLastReviewed, planLastUpdated) &&
        isSameOrAfter(providerLastReviewed, mostRecentlyUpdatedGoal) &&
        isSameOrAfter(providerLastReviewed, cutoffDate);

    const memberLastReviewed = toDate(memberSignatureRequest?.LastReviewedOn);
    const showMemberSignature =
        memberSignatureRequest &&
        memberLastReviewed &&
        planLastUpdated &&
        isSameOrAfter(memberLastReviewed, planLastUpdated) &&
        isSameOrAfter(memberLastReviewed, mostRecentlyUpdatedGoal) &&
        isSameOrAfter(memberLastReviewed, cutoffDate);

    const dispatch = useAppDispatch();
    const handleSign = () => {
        dispatch(setSignatureType(CarePlanSignatureTypeEnum.Provider));
        dispatch(setShowSignatureModal(true));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 24, textAlign: 'center', pb: 0 }}>Care Plan Review & Sign Preview</DialogTitle>
            <DialogContent dividers sx={{ bgcolor: '#f9f9f9', minHeight: 400 }}>
                <Stack spacing={3}>
                    {reviewSignGoals.map((g, idx) => {
                        const relatedStrengths = getRelated(g, 'strength', integratedCarePlan);
                        const relatedBarriers = getRelated(g, 'barrier', integratedCarePlan);
                        const relatedPlans = getRelated(g, 'plan', integratedCarePlan);
                        const relatedNextSteps = getRelated(g, 'nextStep', integratedCarePlan);
                        const relatedMetrics = getRelated(g, 'metric', integratedCarePlan);
                        const relatedSchedulePlans = getRelated(g, 'schedulePlan', integratedCarePlan);
                        // Helper to print updated time if available (only for non-progress items)
                        const printUpdated = (item: any) => {
                            const date = item?.UpdatedAt;
                            return date ? `Last Updated: ${new Date(date).toLocaleString()}` : '';
                        };
                        return (
                            <Box key={g.Id} sx={{ mb: 4, p: 3, bgcolor: '#fff', borderRadius: 3, boxShadow: 3 }}>
                                <Typography variant="h6" fontWeight={700}>
                                    {g.Name}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {printUpdated(g)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Updated By: {g.UpdatedProviderById?.Name || '—'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Status:</strong> {GOAL_STATUSES.find((s) => s.value === g.Status)?.label || '—'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={
                                            (() => {
                                                const dueDate = toDate(g.DueDate);
                                                return dueDate && isBefore(dueDate, new Date()) && g.Status !== CarePlanEnum.Achieved;
                                            })()
                                                ? 'error.main'
                                                : 'text.primary'
                                        }
                                    >
                                        <strong>Due:</strong> {g.DueDate ? new Date(g.DueDate).toLocaleDateString() : '—'}
                                    </Typography>
                                </Stack>
                                {g.Progress && g.Progress.length > 0 && (
                                    <Box sx={{ mb: 2, bgcolor: '#f7f7fa', borderRadius: 1, px: 2, py: 1, border: '1px dashed #bdbdbd' }}>
                                        <Typography variant="body2" fontWeight={500} color="primary">
                                            Goal Progress Notes
                                        </Typography>
                                        <List dense sx={{ pl: 1 }}>
                                            {g.Progress.map((p: any, i: number) => (
                                                <ListItem
                                                    key={p.Id}
                                                    sx={{ pl: 0, borderBottom: i !== g.Progress.length - 1 ? '1px solid #e0e0e0' : 'none', py: 1 }}
                                                >
                                                    <ListItemText
                                                        primary={<span style={{ fontSize: 13 }}>{p.Name}</span>}
                                                        secondary={
                                                            <span
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontStyle: 'italic',
                                                                }}
                                                            >{`${new Date(p.CreatedAt).toLocaleString()} ${
                                                                p.CreatedBy?.Name ? `by ${p.CreatedBy.Name}` : ''
                                                            }`}</span>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="success.main">
                                            Strengths
                                        </Typography>
                                        {relatedStrengths.length > 0 ? (
                                            <List dense>
                                                {relatedStrengths.map((s: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={s.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`${s.Name}`}
                                                                secondary={<span style={{ fontSize: 12, fontStyle: 'italic' }}>{printUpdated(s)}</span>}
                                                            />
                                                        </ListItem>
                                                        {s.Progress && s.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {s.Progress.map((p: any, i: number) => (
                                                                    <ListItem key={p.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={<span style={{ fontSize: 12, color: '#1976d2' }}>Progress: {p.Name}</span>}
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    p.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="error.main">
                                            Barriers
                                        </Typography>
                                        {relatedBarriers.length > 0 ? (
                                            <List dense>
                                                {relatedBarriers.map((b: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={b.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`${b.Name}`}
                                                                secondary={<span style={{ fontSize: 12, fontStyle: 'italic' }}>{printUpdated(b)}</span>}
                                                            />
                                                        </ListItem>
                                                        {b.Progress && b.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {b.Progress.map((p: any, i: number) => (
                                                                    <ListItem key={p.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={<span style={{ fontSize: 12, color: '#d32f2f' }}>Progress: {p.Name}</span>}
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    p.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="info.dark">
                                            Plans
                                        </Typography>
                                        {relatedPlans.length > 0 ? (
                                            <List dense>
                                                {relatedPlans.map((p: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={p.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`${p.Name}`}
                                                                secondary={<span style={{ fontSize: 12, fontStyle: 'italic' }}>{printUpdated(p)}</span>}
                                                            />
                                                        </ListItem>
                                                        {p.Progress && p.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {p.Progress.map((pr: any, i: number) => (
                                                                    <ListItem key={pr.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <span style={{ fontSize: 12, color: '#0288d1' }}>Progress: {pr.Name}</span>
                                                                            }
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    p.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="secondary.main">
                                            Next Steps
                                        </Typography>
                                        {relatedNextSteps.length > 0 ? (
                                            <List dense>
                                                {relatedNextSteps.map((m: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={m.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`${m.Name}`}
                                                                secondary={<span style={{ fontSize: 12, fontStyle: 'italic' }}>{printUpdated(m)}</span>}
                                                            />
                                                        </ListItem>
                                                        {m.Progress && m.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {m.Progress.map((pr: any, i: number) => (
                                                                    <ListItem key={pr.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <span style={{ fontSize: 12, color: '#7b1fa2' }}>Progress: {pr.Name}</span>
                                                                            }
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    pr.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="info.light">
                                            Metrics
                                        </Typography>
                                        {relatedMetrics.length > 0 ? (
                                            <List dense>
                                                {relatedMetrics.map((m: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={m.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`${m.Name}`}
                                                                secondary={<span style={{ fontSize: 12, fontStyle: 'italic' }}>{printUpdated(m)}</span>}
                                                            />
                                                        </ListItem>
                                                        {m.Progress && m.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {m.Progress.map((pr: any, i: number) => (
                                                                    <ListItem key={pr.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <span style={{ fontSize: 12, color: '#388e3c' }}>Progress: {pr.Name}</span>
                                                                            }
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    pr.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                                            Schedule Plans
                                        </Typography>
                                        {relatedSchedulePlans.length > 0 ? (
                                            <List dense>
                                                {relatedSchedulePlans.map((sp: any) => (
                                                    <Paper sx={{ px: 1, mb: 1 }} elevation={3} key={sp.Id}>
                                                        <ListItem sx={{ pl: 0 }}>
                                                            <ListItemText
                                                                primary={`Appointment with ${sp.Provider?.Role || '—'} occurs every ${sp.TimeIntervalIndays} ${
                                                                    TimeIntervalTypeEnum[sp.TimeIntervalType]
                                                                }`}
                                                                secondary={printUpdated(sp)}
                                                            />
                                                        </ListItem>
                                                        {sp.Progress && sp.Progress.length > 0 && (
                                                            <List disablePadding sx={{ pl: 2, mb: 1 }}>
                                                                {sp.Progress.map((pr: any, i: number) => (
                                                                    <ListItem key={pr.Id} sx={{ pl: 0, py: 0.2 }}>
                                                                        <ListItemText
                                                                            primary={
                                                                                <span style={{ fontSize: 12, color: '#fbc02d' }}>Progress: {pr.Name}</span>
                                                                            }
                                                                            secondary={
                                                                                <span style={{ fontSize: 12, fontStyle: 'italic' }}>{`${new Date(
                                                                                    pr.CreatedAt
                                                                                ).toLocaleString()}`}</span>
                                                                            }
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        )}
                                                    </Paper>
                                                ))}
                                            </List>
                                        ) : (
                                            <Typography color="text.secondary" fontStyle="italic">
                                                None
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                    {(showProviderSignature || showMemberSignature) && (
                        <Grid container>
                            <Grid size={12}>
                                <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }} elevation={3}>
                                    <Stack direction={'row'} justifyContent={'center'} spacing={2}>
                                        {showProviderSignature && (
                                            <Stack spacing={0} alignItems="center">
                                                {providerSignatureRequest.Signature && (
                                                    <img
                                                        src={providerSignatureRequest.Signature || ''}
                                                        alt="Signature"
                                                        style={{ width: 100, height: 50, objectFit: 'contain' }}
                                                    />
                                                )}
                                                <Typography variant="subtitle2" color="primary.main">
                                                    {CarePlanSignatureTypeEnum[providerSignatureRequest.SignatureType] || '—'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {providerSignatureRequest.LastReviewedBy?.Name || '—'}
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {`${new Date(providerSignatureRequest.LastReviewedOn).toLocaleString()}` || '—'}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {showMemberSignature && (
                                            <Stack spacing={0} alignItems="center">
                                                {memberSignatureRequest.SignatureType !== CarePlanSignatureTypeEnum.VerbalConsent && (
                                                    <img
                                                        src={memberSignatureRequest.Signature || ''}
                                                        alt="Signature"
                                                        style={{ width: 100, height: 50, objectFit: 'contain' }}
                                                    />
                                                )}
                                                <Typography variant="subtitle2" color="primary.main">
                                                    {CarePlanSignatureTypeEnum[memberSignatureRequest.SignatureType] || '—'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {memberSignatureRequest.LastReviewedBy?.Name || '—'}
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {`${new Date(memberSignatureRequest.LastReviewedOn).toLocaleString()}` || '—'}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-evenly' }}>
                    <Button onClick={onClose} variant="outlined" color="primary" sx={{ minWidth: 120 }}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={showMemberSignature}
                        onClick={() => {
                            dispatch(handleSendToMember(memberId, true));
                        }}
                    >
                        Get Signature from Tablet
                    </Button>
                    <Button sx={{ minWidth: '120px' }} variant="contained" color="primary" onClick={handleSign}>
                        Sign
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewSignModal;
